import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../lib/ApiProvider';
import type { MandantCreateInput, MandantListParams, MandantUpdateInput } from '../../lib/api';

export const mandantKeys = {
  all: ['mandants'] as const,
  list: (params: MandantListParams) => ['mandants', 'list', params] as const,
  detail: (id: string) => ['mandants', 'detail', id] as const,
};

export function useMandants(params: MandantListParams) {
  const api = useApi();
  return useQuery({
    queryKey: mandantKeys.list(params),
    queryFn: () => api.listMandants(params),
  });
}

export function useMandant(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: mandantKeys.detail(id ?? ''),
    queryFn: () => api.getMandant(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateMandant() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MandantCreateInput) => api.createMandant(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: mandantKeys.all }),
  });
}

export function useUpdateMandant(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MandantUpdateInput) => api.updateMandant(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: mandantKeys.all }),
  });
}

export function useDeactivateMandant(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.deactivateMandant(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: mandantKeys.all }),
  });
}
